import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  GoogleAuthProvider,
  GithubAuthProvider,
  OAuthProvider,
  connectAuthEmulator,
  browserLocalPersistence,
  browserSessionPersistence,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
} from 'firebase/auth';

// Environment detection for Firebase config
const isProductionEnvironment = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'aiprep.io' ||
         hostname === 'www.aiprep.io' ||
         hostname === 'app.aiprep.io';
};

// Firebase configuration - environment-aware
// Production uses aiprep-prod, development uses aiprep-465906
// IMPORTANT: Each Firebase project has its own set of credentials
const getFirebaseConfig = () => {
  if (isProductionEnvironment()) {
    // Production Firebase configuration (aiprep-prod)
    // These values MUST come from environment variables set during build
    // They are project-specific and cannot be shared with dev
    const prodApiKey = process.env.REACT_APP_FIREBASE_API_KEY_PROD;
    const prodAppId = process.env.REACT_APP_FIREBASE_APP_ID_PROD;
    const prodMessagingSenderId = process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID_PROD;
    const prodMeasurementId = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID_PROD;

    // Validate required production credentials - fail fast if missing
    const missingVars: string[] = [];
    if (!prodApiKey) missingVars.push('REACT_APP_FIREBASE_API_KEY_PROD');
    if (!prodAppId) missingVars.push('REACT_APP_FIREBASE_APP_ID_PROD');
    if (!prodMessagingSenderId) missingVars.push('REACT_APP_FIREBASE_MESSAGING_SENDER_ID_PROD');

    if (missingVars.length > 0) {
      throw new Error(
        'CRITICAL: Production Firebase credentials not configured!\n\n' +
        'Missing environment variables:\n' +
        missingVars.map(v => `  - ${v}`).join('\n') + '\n\n' +
        'These must be set during the frontend build for production.\n' +
        'Get these values from Firebase Console > Project Settings for aiprep-prod.'
      );
    }

    return {
      apiKey: prodApiKey,
      // CRITICAL: authDomain MUST match the hosting domain where users access the app
      // Using aiprep.io ensures OAuth redirects work correctly (cookies/session stay on same domain)
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN_PROD || "aiprep.io",
      projectId: "aiprep-prod",
      storageBucket: "aiprep-prod.firebasestorage.app",
      messagingSenderId: prodMessagingSenderId,
      appId: prodAppId,
      measurementId: prodMeasurementId || ""
    };
  }

  // Development Firebase configuration (aiprep-465906)
  // These are the dev project credentials - safe to have as defaults
  // IMPORTANT: authDomain must match the hosting domain to avoid third-party cookie blocking
  // dev.aiprep.io is a custom domain pointing to Firebase Hosting, with OAuth redirect URI registered
  return {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyBY_0rqqYVJFQQyoMu9HWVlYTOHreqwcdA",
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "dev.aiprep.io",
    projectId: "aiprep-465906",
    storageBucket: "aiprep-465906.firebasestorage.app",
    messagingSenderId: "571284112818",
    appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:571284112818:web:637b4a15d106c81f9c50ab",
    measurementId: "G-XJRN2SEYR5"
  };
};

const firebaseConfig = getFirebaseConfig();

// Initialize Firebase
const app: FirebaseApp = initializeApp(firebaseConfig);

// Initialize Authentication
export const auth: Auth = getAuth(app);

// Set persistence based on user preference (can be toggled)
export const setPersistence = (rememberMe: boolean) => {
  return auth.setPersistence(
    rememberMe ? browserLocalPersistence : browserSessionPersistence
  );
};

// Configure Google Provider
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account',
  access_type: 'offline',
  include_granted_scopes: 'true'
});

// Add required scopes
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
googleProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');

// Configure GitHub Provider
export const githubProvider = new GithubAuthProvider();
githubProvider.addScope('user:email');
githubProvider.addScope('read:user');
githubProvider.setCustomParameters({
  // Allow users to select which account to use
  allow_signup: 'true'
});

// Configure Apple Provider
export const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');
appleProvider.setCustomParameters({
  // Localization
  locale: 'en'
});

// Connect to emulator in development
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_AUTH_EMULATOR === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  console.log('🔐 Connected to Firebase Auth Emulator');
}

// Export auth methods for emulator dev login
export {
  app,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
};